import interpolateComponents from 'interpolate-components';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { Link } from 'src/common/components/shared/link/Link';
import { selectIsSubscriptionEnabled } from 'src/common/selectors/config';
import { HELP_CONTACT_URL } from '../../constants/cmsLinks';
import {
  DISCLAIMER,
  ERROR_ALREADY_CLAIMED,
  ERROR_EXPIRED,
  ERROR_GENERAL,
  ERROR_INVALID,
  ERROR_PREMIUM_USER,
  ERROR_SUBSCRIPTION_DISABLED,
  FEATURELIST,
  FEATURELIST_HEADLINE,
} from '../../constants/localizations/coupon';
import {
  LINE_BREAK_INSTRUCTION,
  LINE_BREAK_INTERPOLATE,
  LOGO_TITLE,
} from '../../constants/localizations/shared';
import { COUPON_QUERY_PARAM, SOURCE_IMAGE_URL_QUERY_PARAM } from '../../constants/queryParams';
import { statuses } from '../../constants/subscription/coupon';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { selectIsUserSubscribed } from '../../selectors/me';
import cssVariables from '../../styles/variables';
import getOverrideOrDefaultResourceKey from '../../utils/getOverrideOrDefaultResourceKey';
import HeroHeader from '../shared/hero/HeroHeader';
import HeroImage from '../shared/hero/HeroImage';
import HeroImageContainer from '../shared/hero/HeroImageContainer';
import HeroText from '../shared/hero/HeroText';
import UnderlineLink from '../shared/link/UnderlineLink';
import ImageBlock from '../shared/parallax/ImageBlock';
import TuneInMarquee from '../shared/svgIcons/TuneInMarquee';
import CouponInput from './CouponInput';
import css from './coupon.module.scss';
import { getDefaultHeroMetadata, imageBlockMetadata } from './metadata';

class Coupon extends Component {
  static propTypes = {
    errorMessage: PropTypes.string,
    disableInput: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  getFeatureList() {
    const { getLocalizedText, location } = this.context;
    return getLocalizedText(
      getOverrideOrDefaultResourceKey(getLocalizedText, location, FEATURELIST),
    )
      .split(LINE_BREAK_INSTRUCTION)
      .filter((line) => line);
  }

  getErrorMessage(errorMessage) {
    const { getLocalizedText, location } = this.context;
    const localizedErrorMessage = getLocalizedText(
      getOverrideOrDefaultResourceKey(getLocalizedText, location, errorMessage),
    );

    if (errorMessage === ERROR_GENERAL) {
      return interpolateComponents({
        mixedString: localizedErrorMessage,
        components: {
          a: (
            <UnderlineLink
              data-testid="supportLink"
              className={css.supportLink}
              to={HELP_CONTACT_URL}
              isNotReactRouterLink
              isDropDown
            />
          ),
        },
      });
    }

    return localizedErrorMessage;
  }

  render() {
    const { getLocalizedText, location } = this.context;
    const { errorMessage, disableInput } = this.props;
    const imageUrl = get(location, ['query', SOURCE_IMAGE_URL_QUERY_PARAM]);
    const heroMetadata = getDefaultHeroMetadata();
    heroMetadata.line1 = getOverrideOrDefaultResourceKey(
      getLocalizedText,
      location,
      heroMetadata.line1,
    );
    heroMetadata.line2 = getOverrideOrDefaultResourceKey(
      getLocalizedText,
      location,
      heroMetadata.line2,
    );
    heroMetadata.line3 = getOverrideOrDefaultResourceKey(
      getLocalizedText,
      location,
      heroMetadata.line3,
    );
    if (imageUrl) {
      heroMetadata.imageUrl = imageUrl;
    }
    /* LOGO TITLE */
    const logoTitleList = getLocalizedText(LOGO_TITLE).replace(
      LINE_BREAK_INSTRUCTION,
      LINE_BREAK_INTERPOLATE,
    );
    const initialValues = {
      couponCode: get(location, ['query', COUPON_QUERY_PARAM], ''),
    };
    const headlineKey = getOverrideOrDefaultResourceKey(
      getLocalizedText,
      location,
      FEATURELIST_HEADLINE,
    );
    const disclaimerKey = getOverrideOrDefaultResourceKey(getLocalizedText, location, DISCLAIMER);
    return (
      <div className={css.container}>
        <section className={css.topSection} style={{ backgroundColor: heroMetadata.bgColor }}>
          <Helmet>
            <title>TuneIn Coupon Entry</title>
          </Helmet>
          <HeroHeader dataTestId="couponHeader">
            <div data-testid="couponLogoLink" className={css.logoContainer}>
              <Link to="/" data-testid="link">
                <TuneInMarquee className={css.logo} dataTestId="logo" />
                <div className={css.logoTitle} data-testid="logoTitle">
                  {interpolateComponents({
                    mixedString: logoTitleList,
                    components: {
                      linebreak: <br />,
                    },
                  })}
                </div>
              </Link>
            </div>
            <div data-testid="couponHeroTextWrapper" className={css.textWrapper}>
              <HeroText element={heroMetadata} highlightColor={cssVariables['--t-sharp']} />
            </div>
            <CouponInput disabled={disableInput} initialValues={initialValues} />
            <div className={css.errorContainer}>
              {errorMessage && (
                <p data-testid="errorMessage" className={css.errorMessage}>
                  {this.getErrorMessage(errorMessage)}
                </p>
              )}
            </div>
            <div className={css.heroImageSection}>
              <HeroImageContainer>
                <HeroImage element={heroMetadata} dataTestId="couponHeroImage" />
              </HeroImageContainer>
            </div>
          </HeroHeader>
        </section>
        <section className={css.bottomSection}>
          <div className={css.featureContainer}>
            <div className={css.imageContainer}>
              <ImageBlock
                dataTestId="coupon"
                imageBlock={imageBlockMetadata}
                isFirstImageSmall={false}
              />
            </div>
            <div className={css.featureList}>
              <h2 data-testid="featureListHeadline">{getLocalizedText(headlineKey)}</h2>
              <ul>
                {this.getFeatureList().map((line, index) => (
                  <li key={index} data-testid={`featureListLine${index}`}>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p data-testid="disclaimer" className={css.disclaimer}>
            {getLocalizedText(disclaimerKey)}
          </p>
        </section>
      </div>
    );
  }
}

function getCodeError({ hasFailed, status }) {
  if (hasFailed) {
    if (status === statuses.invalid) {
      return ERROR_INVALID;
      // TODO: get design's input for maxed_out situation/redeemed
    }
    if (status === statuses.inactive || status === statuses.expired) {
      // It seems that Recurly can return either an `inactive` or `expired` status for a coupon
      // code that expires...so we'll account for both.
      return ERROR_EXPIRED;
    }
    if (status === statuses.maxedOut) {
      return ERROR_ALREADY_CLAIMED;
    }

    return ERROR_GENERAL;
  }

  return null;
}

function mapStateToProps(state) {
  const couponState = get(state, 'subscription.coupon', {});
  const isUserSubscribed = selectIsUserSubscribed(state);
  const isSubscriptionEnabled = selectIsSubscriptionEnabled(state);
  const disableInput = isUserSubscribed || !isSubscriptionEnabled;

  const errorMessage =
    (isUserSubscribed && ERROR_PREMIUM_USER) ||
    (!isSubscriptionEnabled && ERROR_SUBSCRIPTION_DISABLED) ||
    getCodeError(couponState);

  return {
    errorMessage,
    disableInput,
  };
}

export default connect(mapStateToProps)(Coupon);
